import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaEye, FaPrint, FaFileExcel, FaFilePdf, FaTimes } from 'react-icons/fa';
import { getIncomeStatement, clearIncomeStatement, clearError, clearSuccess } from '../../../../redux/Slices/AccountingReportsSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  waitForRender,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

const IncomeStatement = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { incomeStatement, isLoading, error, success } = useSelector((state) => state.accountingReports);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);

  useEffect(() => {
    setTitle('accountingReports.incomeStatementTitle', { defaultValue: 'قائمة الدخل' });
    // Load data automatically on mount
    dispatch(getIncomeStatement({}));
    return () => {
      dispatch(clearIncomeStatement());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success && isFilterApplied) {
      const translatedMessage = t(success, { defaultValue: success });
      toast.success(translatedMessage, { rtl: isRTL });
      dispatch(clearSuccess());
      setIsFilterApplied(false);
    } else if (success && !isFilterApplied) {
      // Clear success message silently if not from filter
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, t, isFilterApplied]);

  useEffect(() => {
    if (error) {
      toast.error(t(error) || error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL, t]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsFilterApplied(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    dispatch(getIncomeStatement(params));
  };

  const preparePdfPages = () => {
    const data = incomeStatement?.data || incomeStatement || {};
    const revenuesData = data.revenues || [];
    const expensesData = data.expenses || [];

    const buildSectionPages = (sectionName, sectionItems) => {
      const topHeader = document.getElementById(`income-statement-pdf-measure-${sectionName}-top-header`);
      const thead = document.getElementById(`income-statement-pdf-measure-${sectionName}-thead`);
      const footer = document.getElementById('income-statement-pdf-measure-footer');
      const tbody = document.getElementById(`income-statement-pdf-measure-${sectionName}-tbody`);
      const chunks = calculatePdfPagination(sectionItems, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
      return chunks.map((chunk, idx) => ({
        section: sectionName,
        rows: chunk.items || chunk,
        startIndex: chunk.startIndex ?? idx * 10,
        isLastSectionPage: idx === chunks.length - 1,
      }));
    };

    let pages = [];
    if (revenuesData.length > 0 || expensesData.length > 0) {
      const revenuePages = buildSectionPages('revenues', revenuesData);
      const expensePages = buildSectionPages('expenses', expensesData);
      pages = [...revenuePages, ...expensePages];
    }

    if (pages.length === 0) {
      pages = [
        {
          section: 'revenues',
          rows: [],
          startIndex: 0,
          isLastSectionPage: true,
        },
      ];
    }

    return pages.map((page, pageIndex) => ({
      ...page,
      pageNumber: pageIndex + 1,
      isFirstPage: pageIndex === 0,
      isLastPage: pageIndex === pages.length - 1,
    }));
  };

  const handlePreviewAll = () => {
    if (!incomeStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const data = incomeStatement?.data || incomeStatement || {};
    if (!data.revenues && !data.expenses) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const chunks = preparePdfPages();
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  const handlePrintAll = async () => {
    if (!incomeStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setPdfType('all');
      const chunks = preparePdfPages();
      setPdfChunks(chunks);

      await waitForRender(200);
      setIsGeneratingPdf(true);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk).
      // Templates may render under either id depending on preview state.
      const elementIds = chunks.map((_, i) =>
        document.getElementById(`income-statement-pdf-all-${i}`)
          ? `income-statement-pdf-all-${i}`
          : `preview-income-statement-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds,
        fileName: datedFileName('ACE_IncomeStatement'),
        isRTL,
      });
      toast.success(t('accountingReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('accountingReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!incomeStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const data = incomeStatement?.data || incomeStatement || {};
      const revenues = data.revenues || [];
      const expenses = data.expenses || [];

      const excelData = [
        {
          [t('accountingReports.period', { defaultValue: 'الفترة' })]: `${formatDate(startDate)} - ${formatDate(endDate)}`,
        },
        {},
        {
          [t('accountingReports.code', { defaultValue: 'الكود' })]: t('accountingReports.revenues', { defaultValue: 'الإيرادات' }),
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: '',
          [t('accountingReports.balance', { defaultValue: 'المبلغ' })]: '',
        },
        ...revenues.map((item) => ({
          [t('accountingReports.code', { defaultValue: 'الكود' })]: item.code || '—',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar),
          [t('accountingReports.balance', { defaultValue: 'المبلغ' })]: formatNumber(item.balance || 0),
        })),
        {
          [t('accountingReports.code', { defaultValue: 'الكود' })]: '',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: t('accountingReports.totalRevenue', { defaultValue: 'إجمالي الإيرادات' }),
          [t('accountingReports.balance', { defaultValue: 'المبلغ' })]: formatNumber(data.total_revenue || 0),
        },
        {},
        {
          [t('accountingReports.code', { defaultValue: 'الكود' })]: t('accountingReports.expenses', { defaultValue: 'المصروفات' }),
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: '',
          [t('accountingReports.balance', { defaultValue: 'المبلغ' })]: '',
        },
        ...expenses.map((item) => ({
          [t('accountingReports.code', { defaultValue: 'الكود' })]: item.code || '—',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar),
          [t('accountingReports.balance', { defaultValue: 'المبلغ' })]: formatNumber(item.balance || 0),
        })),
        {
          [t('accountingReports.code', { defaultValue: 'الكود' })]: '',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: t('accountingReports.totalExpenses', { defaultValue: 'إجمالي المصروفات' }),
          [t('accountingReports.balance', { defaultValue: 'المبلغ' })]: formatNumber(data.total_expenses || 0),
        },
        {},
        {
          [t('accountingReports.code', { defaultValue: 'الكود' })]: '',
          [t('accountingReports.name', { defaultValue: 'الاسم' })]: t('accountingReports.netProfitLoss', { defaultValue: 'صافي الربح/الخسارة' }),
          [t('accountingReports.balance', { defaultValue: 'المبلغ' })]: formatNumber(data.net_profit_loss || 0),
        },
      ];

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Income Statement');
      XLSX.writeFile(workbook, `IncomeStatement_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('accountingReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    );
  };

  const reportData = incomeStatement?.data || incomeStatement || {};
  const revenues = reportData.revenues || [];
  const expenses = reportData.expenses || [];
  const revenueRows = revenues.length + 1;
  const expensesHeaderRows = 2;
  const maxRowsFirstPage = 16;
  const availableRowsForExpenses = Math.max(0, maxRowsFirstPage - revenueRows - expensesHeaderRows);
  const firstPageExpenses = expenses.slice(0, availableRowsForExpenses);
  const secondPageExpenses = expenses.slice(availableRowsForExpenses);

  return (
    <div style={{ padding: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h2 style={{ color: 'white', marginBottom: '20px', direction: dir, textAlign }}>
          {t('accountingReports.incomeStatement', { defaultValue: 'قائمة الدخل' })}
        </h2>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{
          backgroundColor: '#1a1f2e',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.startDate', { defaultValue: 'تاريخ البداية' })}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.endDate', { defaultValue: 'تاريخ النهاية' })}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? t('accountingReports.loading', { defaultValue: 'جاري التحميل...' }) : t('accountingReports.view', { defaultValue: 'عرض' })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {incomeStatement && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {incomeStatement && (
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '20px',
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ccc', marginBottom: '20px', direction: dir, textAlign }}>
              <div><strong>{t('accountingReports.period', { defaultValue: 'الفترة' })}:</strong> {formatDate(startDate)} - {formatDate(endDate)}</div>
            </div>

            {/* Revenues Table */}
            {reportData.revenues && reportData.revenues.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', direction: dir, textAlign }}>
                  {t('accountingReports.revenues', { defaultValue: 'الإيرادات' })}
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1b3350' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#AC2000' }}>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', width: '48px' }}>
                          #
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {t('accountingReports.code', { defaultValue: 'الكود' })}
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {t('accountingReports.name', { defaultValue: 'الاسم' })}
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {t('accountingReports.balance', { defaultValue: 'المبلغ' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.revenues.map((item, idx) => (
                        <tr key={item.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e' }}>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {item.code || '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'white', border: '1px solid #333' }}>
                            {isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {formatNumber(item.balance)}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: '#1f2937', fontWeight: 'bold' }}>
                        <td colSpan={3} style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', direction: dir }}>
                          {t('accountingReports.totalRevenue', { defaultValue: 'إجمالي الإيرادات' })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {formatNumber(reportData.total_revenue || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Expenses Table */}
            {reportData.expenses && reportData.expenses.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', direction: dir, textAlign }}>
                  {t('accountingReports.expenses', { defaultValue: 'المصروفات' })}
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1b3350' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#AC2000' }}>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', width: '48px' }}>
                          #
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {t('accountingReports.code', { defaultValue: 'الكود' })}
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {t('accountingReports.name', { defaultValue: 'الاسم' })}
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {t('accountingReports.balance', { defaultValue: 'المبلغ' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.expenses.map((item, idx) => (
                        <tr key={item.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e' }}>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {item.code || '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'white', border: '1px solid #333' }}>
                            {isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {formatNumber(item.balance)}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: '#1f2937', fontWeight: 'bold' }}>
                        <td colSpan={3} style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', direction: dir }}>
                          {t('accountingReports.totalExpenses', { defaultValue: 'إجمالي المصروفات' })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {formatNumber(reportData.total_expenses || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Summary */}
            <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#1b3350', borderRadius: '8px' }}>
              <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', direction: dir, textAlign }}>
                {t('accountingReports.summary', { defaultValue: 'الملخص' })}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                <div style={{ padding: '15px', backgroundColor: '#1a1f2e', borderRadius: '6px' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.totalRevenue', { defaultValue: 'إجمالي الإيرادات' })}
                  </div>
                  <div style={{ color: '#28a745', fontSize: '20px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(reportData.total_revenue || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1a1f2e', borderRadius: '6px' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.totalExpenses', { defaultValue: 'إجمالي المصروفات' })}
                  </div>
                  <div style={{ color: '#dc3545', fontSize: '20px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(reportData.total_expenses || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1a1f2e', borderRadius: '6px' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.netProfitLoss', { defaultValue: 'صافي الربح/الخسارة' })}
                  </div>
                  <div style={{
                    color: (reportData.net_profit_loss || 0) >= 0 ? '#28a745' : '#dc3545',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    direction: dir,
                    textAlign
                  }}>
                    {formatNumber(reportData.net_profit_loss || 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PDF Print Templates (one full page per chunk) */}
      {pdfType === 'all' && incomeStatement && pdfChunks && pdfChunks.length > 0 && (
        pdfChunks.map((page, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`income-statement-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '794px',
              height: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
          >
            <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px' }}>
              <div style={{ border: '2px solid #000', height: '1033px', padding: '18px 18px 10px', position: 'relative', display: 'flex', flexDirection: 'column', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '95%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} crossOrigin="anonymous" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>{t('accountingReports.incomeStatementTitle', { defaultValue: 'قائمة الدخل' })}</h2>
                      <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>{formatDate(startDate)} - {formatDate(endDate)}</div>
                    </div>
                    <div style={{ color: '#000', fontSize: '12px' }}>
                      {page.section === 'revenues' && page.rows.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center', direction: dir }}>{t('accountingReports.revenues', { defaultValue: 'الإيرادات' })}</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: '15px' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>#</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.code', { defaultValue: 'الكود' })}</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.name', { defaultValue: 'الاسم' })}</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.balance', { defaultValue: 'المبلغ' })}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {page.rows.map((item, idx) => (
                                <tr key={item.id || idx} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{page.startIndex + idx + 1}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{item.code || '—'}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>{isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(item.balance || 0)}</td>
                                </tr>
                              ))}
                              {page.isLastSectionPage && (
                                <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                  <td colSpan={3} style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.totalRevenue', { defaultValue: 'إجمالي الإيرادات' })}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(reportData.total_revenue || 0)}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {page.section === 'expenses' && page.rows.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center', direction: dir }}>{t('accountingReports.expenses', { defaultValue: 'المصروفات' })}</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: '15px' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>#</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.code', { defaultValue: 'الكود' })}</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.name', { defaultValue: 'الاسم' })}</th>
                                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.balance', { defaultValue: 'المبلغ' })}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {page.rows.map((item, idx) => (
                                <tr key={item.id || idx} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{page.startIndex + idx + 1}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{item.code || '—'}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>{isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(item.balance || 0)}</td>
                                </tr>
                              ))}
                              {page.isLastSectionPage && (
                                <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                  <td colSpan={3} style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.totalExpenses', { defaultValue: 'إجمالي المصروفات' })}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(reportData.total_expenses || 0)}</td>
                                </tr>
                              )}
                              {page.isLastPage && (
                                <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                  <td colSpan={3} style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: '#000080' }}>{t('accountingReports.netProfitLoss', { defaultValue: 'صافي الربح / الخسارة' })}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', color: '#000080' }}>{formatNumber(reportData.net_profit_loss || 0)}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                  <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px', flexShrink: 0, display: 'block' }} crossOrigin="anonymous" />
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Hidden measurement templates used to calculate dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="income-statement-pdf-measure-revenues-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{t('accountingReports.incomeStatementTitle', { defaultValue: 'قائمة الدخل' })}</h2>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>{formatDate(startDate)} - {formatDate(endDate)}</div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="income-statement-pdf-measure-revenues-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.code', { defaultValue: 'الكود' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.name', { defaultValue: 'الاسم' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.balance', { defaultValue: 'المبلغ' })}</th>
            </tr>
          </thead>
          <tbody id="income-statement-pdf-measure-revenues-tbody">
            {revenues.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.code || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div id="income-statement-pdf-measure-expenses-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{t('accountingReports.incomeStatementTitle', { defaultValue: 'قائمة الدخل' })}</h2>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>{formatDate(startDate)} - {formatDate(endDate)}</div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="income-statement-pdf-measure-expenses-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.code', { defaultValue: 'الكود' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.name', { defaultValue: 'الاسم' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.balance', { defaultValue: 'المبلغ' })}</th>
            </tr>
          </thead>
          <tbody id="income-statement-pdf-measure-expenses-tbody">
            {expenses.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.code || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/*
          Reserve space for the net-profit-loss summary block that only renders on the
          very last page, so pagination never lets expense rows overflow under the
          fixed-position footer on that final page.
        */}
        <div id="income-statement-pdf-measure-footer" style={{ height: '120px' }}>
          <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #000' }}>
            <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '10px', fontSize: '16px' }}>
              {t('accountingReports.summary', { defaultValue: 'الملخص' })}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '10px', width: '50%', fontWeight: 'bold', fontSize: '14px' }}>x</td>
                  <td style={{ border: '1px solid #000', padding: '10px', width: '50%', fontSize: '14px' }}>0.00</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '10px', fontWeight: 'bold', fontSize: '14px' }}>x</td>
                  <td style={{ border: '1px solid #000', padding: '10px', fontSize: '14px' }}>0.00</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '10px', fontWeight: 'bold', fontSize: '14px' }}>x</td>
                  <td style={{ border: '1px solid #000', padding: '10px', fontSize: '14px' }}>0.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {isPreviewOpen && incomeStatement && reportData && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            overflow: 'hidden'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsPreviewOpen(false);
            }
          }}
          onWheel={(e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              setPreviewZoom((prev) => Math.max(0.3, Math.min(2, prev + delta)));
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#1a1f2e',
              borderRadius: '12px',
              padding: '20px',
              width: '100%',
              height: '100%',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: '20px' }}>
                {t('accountingReports.incomeStatement', { defaultValue: 'قائمة الدخل' })}
              </h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginRight: '10px' }}>
                  <button
                    onClick={() => setPreviewZoom((prev) => Math.max(0.3, prev - 0.1))}
                    style={{
                      backgroundColor: '#444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}
                    title={t('accountingReports.zoomOut', { defaultValue: 'تصغير' })}
                  >
                    −
                  </button>
                  <span style={{ color: 'white', minWidth: '60px', textAlign: 'center', fontSize: '14px' }}>
                    {Math.round(previewZoom * 100)}%
                  </span>
                  <button
                    onClick={() => setPreviewZoom((prev) => Math.min(2, prev + 0.1))}
                    style={{
                      backgroundColor: '#444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}
                    title={t('accountingReports.zoomIn', { defaultValue: 'تكبير' })}
                  >
                    +
                  </button>
                  <button
                    onClick={() => {
                      setPreviewZoom(1.0);
                      setPreviewPosition({ x: 0, y: 0 });
                    }}
                    style={{
                      backgroundColor: '#444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title={t('accountingReports.fit', { defaultValue: 'ملاءمة الشاشة' })}
                  >
                    {t('accountingReports.fit', { defaultValue: 'ملاءمة' })}
                  </button>
                </div>
                <button
                  onClick={handlePrintAll}
                  disabled={isGeneratingPdf}
                  style={{
                    backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: isGeneratingPdf ? 0.6 : 1
                  }}
                >
                  <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
                </button>
                <button
                  onClick={() => setIsPreviewOpen(false)}
                  style={{
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    cursor: 'pointer'
                  }}
                >
                  {t('accountingReports.close', { defaultValue: 'إغلاق' })}
                </button>
              </div>
            </div>
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                padding: '20px',
                overflow: previewZoom <= 1.0 ? 'auto' : 'hidden',
                flex: 1,
                display: 'flex',
                alignItems: previewZoom <= 1.0 ? 'flex-start' : 'center',
                justifyContent: 'center',
                minHeight: 0,
                position: 'relative',
                cursor: previewZoom > 1.0 && isDragging ? 'grabbing' : previewZoom > 1.0 ? 'grab' : 'default'
              }}
              onMouseDown={(e) => {
                if (previewZoom > 1.0 && e.button === 0) {
                  setIsDragging(true);
                  setDragStart({
                    x: e.clientX - previewPosition.x,
                    y: e.clientY - previewPosition.y
                  });
                }
              }}
              onDragStart={(e) => previewZoom > 1.0 && e.preventDefault()}
            >
              <div
                id="preview-income-statement-pdf-all"
                style={{
                  transform: previewZoom > 1.0
                    ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                    : `scale(${previewZoom})`,
                  transformOrigin: previewZoom <= 1.0 ? 'top center' : 'center center',
                  transition: isDragging ? 'none' : 'transform 0.2s ease',
                  width: '794px',
                  userSelect: previewZoom > 1.0 ? 'none' : 'auto'
                }}
              >
                <div style={{
                  border: '5px solid #000',
                  padding: '3px',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  width: '794px', // Fixed width for A4
                  minHeight: '1123px',
                  height: 'auto',
                  overflow: 'visible'
                }}>
                  <div style={{
                    border: '2px solid #000',
                    minHeight: '1033px',
                    height: '1033px',
                    padding: '18px 18px 10px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility',
                    direction: isRTL ? 'rtl' : 'ltr',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    width: '100%',
                    overflow: 'visible'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: '95%',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.08,
                      zIndex: 0,
                      pointerEvents: 'none'
                    }} />
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          marginBottom: '8px',
                          borderBottom: '4px double #000',
                          paddingBottom: '8px',
                          flexShrink: 0
                        }}
                        crossOrigin="anonymous"
                      />

                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                        <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                            {t('accountingReports.incomeStatementTitle', { defaultValue: 'قائمة الدخل' })}
                          </h2>
                          <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                            {formatDate(startDate)} - {formatDate(endDate)}
                          </div>
                        </div>
                        <div style={{ color: '#000', fontSize: '12px' }}>
                          {/* Revenues Table */}
                          {reportData.revenues && reportData.revenues.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                              <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center', direction: dir }}>
                                {t('accountingReports.revenues', { defaultValue: 'الإيرادات' })}
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: '15px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.code', { defaultValue: 'الكود' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.name', { defaultValue: 'الاسم' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.balance', { defaultValue: 'المبلغ' })}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {reportData.revenues.map((item, idx) => (
                                    <tr key={item.id || idx} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {item.code || '—'}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {formatNumber(item.balance || 0)}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                    <td colSpan={2} style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.totalRevenue', { defaultValue: 'إجمالي الإيرادات' })}
                                    </td>
                                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {formatNumber(reportData.total_revenue || 0)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Expenses Table - First Page (Partial) */}
                          {firstPageExpenses && firstPageExpenses.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                              <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center' }}>
                                {t('accountingReports.expenses', { defaultValue: 'المصروفات' })}
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: '15px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.code', { defaultValue: 'الكود' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.name', { defaultValue: 'الاسم' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.balance', { defaultValue: 'المبلغ' })}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {firstPageExpenses.map((item, idx) => (
                                    <tr key={item.id || idx} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {item.code || '—'}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {formatNumber(item.balance || 0)}
                                      </td>
                                    </tr>
                                  ))}
                                  {secondPageExpenses.length === 0 && (
                                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                      <td colSpan={2} style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {t('accountingReports.totalExpenses', { defaultValue: 'إجمالي المصروفات' })}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {formatNumber(reportData.total_expenses || 0)}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}

                        </div>
                      </div>

                      <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '18px',
                        right: '18px',
                        flexShrink: 0
                      }}>
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderTop: '4px double #000',
                            paddingTop: '8px',
                          }}
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Page (Second Page) - Rendered nicely after the first page if needed, or we just rely on scrolling */}
                <div
                  id="preview-income-statement-pdf-summary"
                  style={{
                    marginTop: '40px',
                    border: '5px solid #000',
                    padding: '3px',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    width: '794px',
                    minHeight: '1123px',
                    height: 'auto',
                    overflow: 'visible'
                  }}>
                  <div style={{
                    border: '2px solid #000',
                    minHeight: '1033px',
                    height: '1033px',
                    padding: '18px 18px 10px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility',
                    direction: isRTL ? 'rtl' : 'ltr',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    width: '100%',
                    overflow: 'visible'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: '95%',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.08,
                      zIndex: 0,
                      pointerEvents: 'none'
                    }} />
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          marginBottom: '8px',
                          borderBottom: '4px double #000',
                          paddingBottom: '8px',
                          flexShrink: 0
                        }}
                        crossOrigin="anonymous"
                      />

                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                        <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                            {t('accountingReports.incomeStatementTitle', { defaultValue: 'قائمة الدخل' })}
                          </h2>
                          <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                            {formatDate(startDate)} - {formatDate(endDate)}
                          </div>
                        </div>

                        <div style={{ color: '#000', fontSize: '12px' }}>
                          {/* Remaining Expenses Table */}
                          {secondPageExpenses && secondPageExpenses.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                              <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center', direction: dir }}>
                                {t('accountingReports.expenses', { defaultValue: 'المصروفات' })} ({t('accountingReports.continued', { defaultValue: 'متابعة' })})
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: '15px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.code', { defaultValue: 'الكود' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.name', { defaultValue: 'الاسم' })}
                                    </th>
                                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.balance', { defaultValue: 'المبلغ' })}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {secondPageExpenses.map((item, idx) => (
                                    <tr key={item.id || idx} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {item.code || '—'}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {isRTL ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)}
                                      </td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                        {formatNumber(item.balance || 0)}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                                    <td colSpan={2} style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {t('accountingReports.totalExpenses', { defaultValue: 'إجمالي المصروفات' })}
                                    </td>
                                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                      {formatNumber(reportData.total_expenses || 0)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Summary */}
                          <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #000' }}>
                            <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '10px', fontSize: '16px' }}>
                              {t('accountingReports.summary', { defaultValue: 'الملخص' })}
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <tbody>
                                <tr>
                                  <td style={{ border: '1px solid #000', padding: '10px', textAlign: isRTL ? 'right' : 'left', width: '50%', fontWeight: 'bold', fontSize: '14px' }}>
                                    {t('accountingReports.totalRevenue', { defaultValue: 'إجمالي الإيرادات' })}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', width: '50%', fontSize: '14px' }}>
                                    {formatNumber(reportData.total_revenue || 0)}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ border: '1px solid #000', padding: '10px', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', fontSize: '14px' }}>
                                    {t('accountingReports.totalExpenses', { defaultValue: 'إجمالي المصروفات' })}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', fontSize: '14px' }}>
                                    {formatNumber(reportData.total_expenses || 0)}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ border: '1px solid #000', padding: '10px', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', fontSize: '14px' }}>
                                    {t('accountingReports.netProfitLoss', { defaultValue: 'صافي الربح/الخسارة' })}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', fontSize: '14px' }}>
                                    {formatNumber(reportData.net_profit_loss || 0)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '18px',
                        right: '18px',
                        flexShrink: 0
                      }}>
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderTop: '4px double #000',
                            paddingTop: '8px',
                          }}
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default IncomeStatement;
